import { find } from "lodash";
import { useEffect, useRef, useState } from "react";
import {
  useLocation,
  useNavigate,
  useSearchParams,
  useParams,
} from "react-router-dom";
import { visuallyHidden } from "@mui/utils";
import {
  Box,
  Button,
  ClickAwayListener,
  Collapse,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { ArrowBackOutlined, FilterList } from "@mui/icons-material";

import { useIsMobile } from "hooks/is-mobile.hook";
import { useSourceSearch } from "../hooks";

// import { ReturnDownIcon } from "assets/icons/ReturnDownIcon";
import PillFilter from "./PillFilter";
import { useIsTab } from "hooks/is-tab.hook";
import Timeframe from "./Timeframe";
import EbscoTimeframe from "./EbscoTimeframe";
import { logEvent } from "services/amplitude.service";
import { getValidDate } from "../../../utils/date-validation.util";
import { searchConfig } from "config";
import { isNotNullOrEmpty } from "utils/string.util";
import AccessibleIconButton from "components/base/AccessibleIconButton";

export default function PrimarySearchBox({ isDarkBackground = false }) {
  const {
    buildSearchUrl,
    currentCategory,
    currentSearchTerm,
    currentSource,
    currentSubCategory,
    currentTimeframe,
    defaultSource,
    defaultTimeframe,
    endDate,
    isActive,
    handleActiveStateChange,
    handleSourceCategoryChange,
    previousPath,
    setPreviousPath,
    sources,
    sourceCategories,
    startDate,
    subCategories,
    timeframes,
    subPublishers,
  } = useSourceSearch({ fetchCredentials: true });
  const theme = useTheme();
  const isMobile = useIsMobile();
  const isTab = useIsTab();
  const input = useRef(null);
  const blurSentinel = useRef(null);
  const primarySearchBox = useRef();
  const focusTimer = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    search: searchParam,
    source: sourceParam,
    category: categoryParam,
    academiaSearch,
  } = useParams();

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(currentSearchTerm);
  const [source, setSource] = useState(currentSource);
  const [category, setCategory] = useState(currentCategory);
  const [localCategories, setLocalCategories] = useState(
    handleSourceCategoryChange(source),
  );
  const [subCategory, setSubCategory] = useState(currentSubCategory);
  const [timeframe, setTimeframe] = useState(currentTimeframe);
  const [localEndDate, setLocalEndDate] = useState(endDate);
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [collapseOpen, setCollapse] = useState(false);
  const [elevation, setElevation] = useState(0);
  const isPartnerPublishers = source === "partner-publishers";
  const isEbscoDatabase = source === "databases" && category === "ebsco";
  const wasEbscoDatabase = useRef(isEbscoDatabase);
  const showCategoryFilter =
    isPartnerPublishers ||
    source === "categories" ||
    source === "rating" ||
    source === "databases";
  const categoryFilterTitle =
    {
      categories: "Categories",
      rating: "Media Bias",
      databases: "Databases",
    }[source] || "Categories";

  useEffect(() => {
    if (wasEbscoDatabase.current && !isEbscoDatabase) {
      setTimeframe("all-time");
    }
    wasEbscoDatabase.current = isEbscoDatabase;
  }, [isEbscoDatabase]);

  const shiftFocusOut = () => {
    if (input.current) {
      input.current.blur();
    }
    if (blurSentinel.current) {
      requestAnimationFrame(() => blurSentinel.current?.focus());
    }
  };

  function handleClose() {
    if (focusTimer.current) {
      clearTimeout(focusTimer.current);
      focusTimer.current = null;
    }
    shiftFocusOut();
    setCollapse(false);
    handleActiveStateChange(false);
  }

  function handleInputOnChange(event) {
    setSearchTerm(event.target.value);
  }

  function handleEndCollapse() {
    if (!collapseOpen) {
      handleActiveStateChange(false);
      shiftFocusOut();
    }
  }

  function handleKeyPress(event) {
    if (event.key === "Enter") {
      handleSubmit();
    }
  }

  function handleFilterChange(value, setFn) {
    setFn(value);
  }

  function isSearchDisabled() {
    if (timeframe === "custom-date") {
      const startDateValid =
        localStartDate && getValidDate(localStartDate) !== null;
      const endDateValid = localEndDate && getValidDate(localEndDate) !== null;
      return !startDateValid || !endDateValid ||
        new Date(localStartDate).getTime() > new Date(localEndDate).getTime();
    }
    return false;
  }

  function handleSubmit(override) {
    if (isSearchDisabled()) {
      return;
    }

    const categories = handleSourceCategoryChange(source);
    const catObj = find(categories, { key: category });
    const to = buildSearchUrl({
      query: typeof override === "string" ? override : searchTerm,
      source,
      category: catObj == null ? null : catObj.key,
      date: timeframe,
      subCategory,
      endDate: localEndDate,
      startDate: localStartDate,
    });
    searchParams.delete("tourSearchOpen");
    setSearchParams(searchParams);
    shiftFocusOut();
    if (to != null) {
      handleClose();
      navigate(to);
    }
  }

  useEffect(() => {
    const newCategories = handleSourceCategoryChange(source);
    setLocalCategories(newCategories);
    if (source === "books" || source === "wiki") {
      handleFilterChange(defaultTimeframe.key, setTimeframe);
    }

    const isValidCategory =
      category && newCategories.some((cat) => cat.key === category);
    if (!isValidCategory && category != null) {
      handleFilterChange(null, setCategory);
    }
  }, [source, category, defaultTimeframe, handleSourceCategoryChange]);

  useEffect(() => {
    if (category !== "academia") {
      handleFilterChange(null, setSubCategory);
    } else {
      handleFilterChange(defaultTimeframe.key, setTimeframe);
    }
  }, [category]);

  useEffect(() => {
    if (isActive) {
      setOpen(true);
      setCollapse(true);
      setElevation(12);
      if (input.current) {
        // Cancellable focus: if the box closes before this fires (click-away,
        // route change, tour), a stray focus leaves the input focused while
        // the collapsed/outlined variant is showing. That floats the label on
        // top of the placeholder — the "stuck" doubled "Start your search"
        // glitch. The cleanup below clears it whenever isActive flips back.
        focusTimer.current = setTimeout(() => input.current?.focus(), 0);
      }
    } else if (!isActive && !collapseOpen) {
      setOpen(false);
      setElevation(0);
      shiftFocusOut();
    } else {
      setOpen(false);
      setCollapse(false);
      setElevation(0);
      shiftFocusOut();
    }
    return () => {
      if (focusTimer.current) {
        clearTimeout(focusTimer.current);
        focusTimer.current = null;
      }
    };
  }, [isActive, collapseOpen]);

  useEffect(() => {
    handleClose();
  }, [location.pathname]);

  useEffect(() => {
    if (!isActive) {
      if (searchParam !== searchTerm) {
        setSearchTerm(searchParam ?? "");
      }
      if (sourceParam !== source) {
        setSource(sourceParam ?? defaultSource.key);
      }
      const categoriesForSource = handleSourceCategoryChange(
        sourceParam ?? source,
      );
      const matchedCategory = categoriesForSource.find(
        (c) => c.label === categoryParam,
      );
      const mappedCategoryKey = matchedCategory ? matchedCategory.key : null;
      if (mappedCategoryKey !== category) {
        setCategory(mappedCategoryKey);
      }

      if (academiaSearch) {
        const matchedSubCategory = subCategories.find(
          (s) => s.label === academiaSearch,
        );
        const mappedSubKey = matchedSubCategory ? matchedSubCategory.key : null;
        if (mappedSubKey !== subCategory) {
          setSubCategory(mappedSubKey);
        }
      } else {
        setSubCategory(null);
      }
    }
    // Removed shiftFocusOut() - it was stealing focus from other inputs on the page
  }, [
    searchParam,
    sourceParam,
    categoryParam,
    academiaSearch,
    handleSourceCategoryChange,
    subCategories,
    isActive,
  ]);

  useEffect(() => {
    if (!isActive) {
      if (searchTerm !== currentSearchTerm && !searchParam) {
        if (currentSearchTerm != null) {
          setSearchTerm(currentSearchTerm);
        } else {
          setSearchTerm("");
        }
      }
      if (source !== currentSource) {
        if (currentSource != null) {
          setSource(currentSource);
        } else {
          setSource(defaultSource.key);
        }
      }
      if (category !== currentCategory && !categoryParam) {
        if (currentCategory != null) {
          handleFilterChange(currentCategory, setCategory);
        } else {
          setCategory(null);
        }
      }
      if (subCategory !== currentSubCategory && !academiaSearch) {
        if (currentSubCategory != null) {
          setSubCategory(currentSubCategory);
        } else {
          setSubCategory(null);
        }
      }
    }
    if (timeframe !== currentTimeframe) {
      if (currentTimeframe != null) {
        setTimeframe(currentTimeframe);
      } else {
        setTimeframe(defaultTimeframe.key);
      }
    }
    if (localStartDate !== startDate) {
      if (startDate != null) {
        setLocalStartDate(startDate);
      }
    }
    if (localEndDate !== endDate) {
      if (endDate != null) {
        setLocalEndDate(endDate);
      }
    }

    const currentPath = decodeURI(
      buildSearchUrl({
        query: currentSearchTerm,
        source: currentSource,
        category: currentCategory,
        date: currentTimeframe,
        subCategory: currentSubCategory,
        endDate: endDate,
        startDate: startDate,
      }),
    );
    const browserPath = decodeURI(
      `${
        location.pathname.endsWith("/")
          ? location.pathname.substring(0, location.pathname.length - 1)
          : location.pathname
      }${location.search}`,
    );
    if (
      location.pathname.includes("/search/") &&
      currentPath != null &&
      currentPath === browserPath &&
      previousPath !== currentPath
    ) {
      logEvent("submit_navbar_search", { url: currentPath });
      setPreviousPath(currentPath);
    }
  }, [
    currentSearchTerm,
    currentSource,
    defaultSource,
    currentCategory,
    sourceCategories,
    currentSubCategory,
    currentTimeframe,
    defaultTimeframe,
    startDate,
    endDate,
    isActive,
  ]);

  useEffect(() => {
    function triggerOpen({ detail: { index, action } }) {
      const transitionEnd = () => {
        primarySearchBox.current.removeEventListener(
          "transitionend",
          transitionEnd,
        );
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent(action, {
              detail: index,
            }),
          );
        }, 100);
      };
      primarySearchBox.current.addEventListener("transitionend", transitionEnd);
      searchParams.set("tourSearchOpen", true);
      setSearchParams(searchParams);
      handleActiveStateChange(true);
    }
    function triggerClose() {
      searchParams.delete("tourSearchOpen");
      setSearchParams(searchParams);
      handleClose();
    }
    function triggerSubmit({ detail }) {
      handleSubmit(detail.term);
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("tour:app:search:results", { detail }),
        );
      }, 200);
    }
    function updateSearchTerm(event) {
      if (event.detail != null && typeof event.detail === "string") {
        setSearchTerm(event.detail);
      }
    }
    window.addEventListener("tour:app:search", triggerSubmit);
    window.addEventListener("tour:app:searchTerm", updateSearchTerm);
    window.addEventListener("tour:app:searchOpen", triggerOpen);
    window.addEventListener("tour:app:searchClose", triggerClose);

    return () => {
      window.removeEventListener("tour:app:search", triggerSubmit);
      window.removeEventListener("tour:app:searchTerm", updateSearchTerm);
      window.removeEventListener("tour:app:searchOpen", triggerOpen);
      window.removeEventListener("tour:app:searchClose", triggerClose);
    };
  }, []);
  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
      onClickAway={(event) => {
        if (!searchParams.has("tourSearchOpen") && open) {
          handleClose();
        }
      }}
    >
      <Box
        tabIndex="-1"
        onKeyDown={(e) => {
          if (e.key === "Escape" && open) handleClose();
        }}
        onKeyUp={handleKeyPress}
      >
        <Box
          ref={blurSentinel}
          tabIndex={-1}
          style={{
            position: "absolute",
            width: 0,
            height: 0,
            overflow: "hidden",
          }}
        />
        <Paper
          id="primary-search-box"
          ref={primarySearchBox}
          elevation={elevation}
          className={open ? "active" : "inactive"}
          sx={{
            position: open ? (isMobile ? "fixed" : "sticky") : "relative",
            bgcolor:
              isDarkBackground && theme.palette.mode === "light"
                ? "common.white"
                : open
                  ? "background.default"
                  : "secondary.dark",
            top: 0,
            // bottom: isMobile ? 0 : "unset",
            left: isMobile ? 0 : "unset",
            width: { xs: "80vw", sm: "30vw" },
            borderRadius: open && isMobile ? 0 : 3,
            // overflow: "hidden",
            px: 2,
            zIndex: theme.zIndex.appBar,
            transition: "0.2s ease all",
            "&.active": {
              width: { xs: "100vw", sm: "50vw" },
              transitionDuration: "0.5s",
            },
            ...(isMobile && { maxHeight: "90vh", overflowY: "auto" }),
          }}
        >
          <TextField
            size="medium"
            inputRef={input}
            className={open ? "active" : "inactive"}
            fullWidth
            variant={open ? "standard" : "outlined"}
            // Only show the placeholder when open. When collapsed the outlined
            // `label` already renders "Start your search" as resting text;
            // having both produces a doubled label if the input is focused.
            placeholder={open ? "Start your search" : ""}
            id="primary-search-box-text-field"
            value={searchTerm}
            onChange={handleInputOnChange}
            label="Start your search"
            slotProps={{
              inputLabel: {
                sx: open || isNotNullOrEmpty(searchTerm) ? visuallyHidden : {},
              },
              input: {
                onClick: () => {
                  if (!isActive) {
                    handleActiveStateChange(true);
                  }
                },
                startAdornment: open && (
                  <InputAdornment position="start">
                    <AccessibleIconButton
                      color="primary"
                      onClick={() => handleClose()}
                      edge="start"
                      title="Cancel Search"
                    >
                      <ArrowBackOutlined
                        sx={{
                          width: "1.6rem",
                          height: "1.6rem",
                        }}
                      />
                    </AccessibleIconButton>
                  </InputAdornment>
                ),
                endAdornment: open ? (
                  <InputAdornment position="end">
                    <Button
                      variant="text"
                      id="primary-search-box-search-button"
                      onClick={handleSubmit}
                      disabled={isSearchDisabled()}
                      edge="end"
                    >
                      Search
                    </Button>
                  </InputAdornment>
                ) : (
                  <InputAdornment position="end">
                    <AccessibleIconButton
                      onClick={() => handleActiveStateChange(true)}
                      edge="end"
                      title="Search Filters"
                    >
                      <FilterList />
                    </AccessibleIconButton>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "&.inactive .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&.active > .MuiInputBase-root": {
                p: 1,
                px: 2,
                mb: 2,
              },
            }}
          />
          <Collapse in={collapseOpen} onExited={handleEndCollapse}>
            <Box
              id="primary-search-box-filters"
              sx={{
                minHeight: "10vh",
                display: "grid",
                gridTemplateColumns: "repeat(12, minmax(min-content, 1fr))",
              }}
            >
              <PillFilter
                title="Source Type"
                filters={sources}
                currentFilter={source}
                handleChange={(value) => {
                  handleFilterChange(value, setSource);
                }}
                required
                keyPrefix="source"
                sx={{
                  gridColumn: { xs: "span 12", md: "span 7" },
                  borderBottom: showCategoryFilter
                    ? `1px solid ${theme.palette.grey[300]}`
                    : null,
                  borderRight: isTab
                    ? "unset"
                    : `1px solid ${theme.palette.grey[300]}`,
                }}
              />
              {isEbscoDatabase ? (
                <EbscoTimeframe
                  frequencies={timeframes}
                  timeframeValue={timeframe}
                  setTimeframeValue={setTimeframe}
                  endDateValue={localEndDate}
                  setEndDateValue={setLocalEndDate}
                  startDateValue={localStartDate}
                  setStartDateValue={setLocalStartDate}
                  sx={{
                    gridColumn: { xs: "span 12", md: "span 5" },
                    borderBottom: `1px solid ${theme.palette.grey[300]}`,
                    p: 2,
                  }}
                />
              ) : (
                <Timeframe
                  frequencies={timeframes}
                  timeframeValue={timeframe}
                  setTimeframeValue={setTimeframe}
                  endDateValue={localEndDate}
                  setEndDateValue={setLocalEndDate}
                  startDateValue={localStartDate}
                  setStartDateValue={setLocalStartDate}
                  disabled={
                    (source === "databases" && category !== "proquest") ||
                    source === "books" ||
                    source === "wiki" ||
                    category === "academia"
                  }
                  isOpen={open}
                  sx={{
                    gridColumn: { xs: "span 12", md: "span 5" },
                    borderBottom: showCategoryFilter
                      ? `1px solid ${theme.palette.grey[300]}`
                      : null,
                    p: 2,
                  }}
                />
              )}
              <Collapse in={showCategoryFilter} sx={{ gridColumn: "span 12" }}>
                <PillFilter
                  title={
                    isPartnerPublishers
                      ? "Partner Publishers"
                      : categoryFilterTitle
                  }
                  filters={
                    isPartnerPublishers
                      ? subPublishers
                      : localCategories
                  }
                  currentFilter={category}
                  handleChange={(value) => {
                    handleFilterChange(value, setCategory);
                  }}
                  keyPrefix={
                    isPartnerPublishers ? "partner-publisher" : "category"
                  }
                  sx={{
                    gridColumn: "span 12",
                    borderBottom:
                      category === "academia"
                        ? `1px solid ${theme.palette.grey[300]}`
                        : null,
                  }}
                />
              </Collapse>
              <Collapse
                in={category === "academia"}
                sx={{
                  gridColumn: "span 12",
                }}
              >
                <PillFilter
                  title="Academic Sources"
                  filters={subCategories}
                  currentFilter={subCategory}
                  keyPrefix="academic-source"
                  handleChange={(value) =>
                    handleFilterChange(value, setSubCategory)
                  }
                />
              </Collapse>
            </Box>
          </Collapse>
          {searchConfig.categories.engine === "Bing" && (
            <Typography
              variant="body3"
              component="div"
              sx={({ palette, mode }) => ({
                position: "absolute",
                bottom: "0.1rem",
                right: "3rem",
                fontWeight: "bold",
                color: palette.grey[600],
                fontSize: "0.8rem",
              })}
            >
              Powered by Microsoft Bing
            </Typography>
          )}
        </Paper>
      </Box>
    </ClickAwayListener>
  );
}
